import { createContext, useContext, useMemo } from "react"
import { useAuth } from "@/hooks/useAuth"
import { useStudioNav } from "@/hooks/use-studio"
import { hasAdminPermission, type AdminPermissionKey, type WorkspaceAccess, type WorkspaceId } from "@/lib/workspaces"
import { hasEventsAccess, isAdmin as userIsAdmin, isOwner } from "@/lib/roles"

export interface WorkspaceContextValue {
  current: WorkspaceId
  available: WorkspaceId[]
  access: WorkspaceAccess
  /** Switch with the overlay; `to` overrides the workspace's remembered page. */
  switchTo: (id: WorkspaceId, to?: string) => void
}

export const WorkspaceContext = createContext<WorkspaceContextValue | null>(null)

/** The open workspace, the ones this person can switch to, and the switch itself. */
export function useWorkspace(): WorkspaceContextValue {
  const ctx = useContext(WorkspaceContext)
  if (!ctx) throw new Error("useWorkspace must be used inside WorkspaceProvider")
  return ctx
}

/** Everything the access rules need, from auth and /studio/access. */
export function useWorkspaceAccess(): WorkspaceAccess {
  const { user, licenses } = useAuth()
  const { canStudio, studioOnly, hideLicenseNav } = useStudioNav()
  return useMemo(
    () => ({
      isAdmin: userIsAdmin(user),
      isOwner: isOwner(user),
      adminPermissions: user?.admin_permissions ?? null,
      ownerOnlyKeys: user?.owner_only_admin_permissions ?? null,
      allowed: userIsAdmin(user) ? null : ((user?.workspaces as WorkspaceId[] | null | undefined) ?? null),
      hasLicenses: !!licenses && licenses.length > 0,
      canStudio,
      studioOnly,
      studioPending: hideLicenseNav && !studioOnly,
      outreachEnabled: !!user?.outreach_enabled,
      eventsEnabled: hasEventsAccess(user),
      promptLogEnabled: !!user?.prompt_log_enabled,
      hasInvoices: user?.has_invoices,
    }),
    [user, licenses, canStudio, studioOnly, hideLicenseNav]
  )
}

/** Whether the signed-in admin may open the admin pages behind `key` (owner = all). */
export function useAdminPermission(key: AdminPermissionKey): boolean {
  return hasAdminPermission(useWorkspaceAccess(), key)
}
